import { ServiceUnavailable } from '@tmi/types/api/responses';

// A content catalog answers 503 + Retry-After until its server-side cache first fills (a few seconds after the
// service starts). A reader that would otherwise show an error or nothing retries once, after the delay the server
// asked for (default 5 s, at most 10 s), and treats a second 503 as a real failure.
export const SERVICE_UNAVAILABLE_DEFAULT_RETRY_SECONDS = 5;
export const SERVICE_UNAVAILABLE_MAX_RETRY_SECONDS = 10;

export interface ServiceUnavailableRetryOptions {
    /** Aborts the wait: the promise rejects with an AbortError and no second attempt is made. */
    signal?: AbortSignal;
    /** Asked after the first 503; false rethrows it without waiting (the caller no longer wants the answer). */
    shouldRetry?: () => boolean;
}

export function serviceUnavailableRetryDelayMs( err: ServiceUnavailable ): number {
    const seconds: number = err.retryAfterSeconds ?? SERVICE_UNAVAILABLE_DEFAULT_RETRY_SECONDS;
    return Math.min( seconds, SERVICE_UNAVAILABLE_MAX_RETRY_SECONDS ) * 1000;
}

/** Resolves after `ms`, or rejects with an AbortError as soon as `signal` aborts. */
function wait( ms: number, signal?: AbortSignal ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        const canceled = (): DOMException => new DOMException( 'The 503 retry was canceled.', 'AbortError' );
        if ( signal?.aborted ) {
            reject( canceled() );
            return;
        }

        const aborted = (): void => {
            clearTimeout( timer );
            reject( canceled() );
        };
        const timer = setTimeout( () => {
            signal?.removeEventListener( 'abort', aborted );
            resolve();
        }, ms );
        signal?.addEventListener( 'abort', aborted, { once: true } );
    } );
}

/**
 * Runs `attempt`; on a ServiceUnavailable, waits its Retry-After and runs it exactly once more. Any other failure, and
 * the second attempt's result or failure, is passed through unchanged.
 */
export async function retryOnceOnServiceUnavailable< T >( attempt: () => Promise< T >,
                                                          options: ServiceUnavailableRetryOptions = {} ): Promise< T > {
    try {
        return await attempt();
    } catch ( err: unknown ) {
        if ( !( err instanceof ServiceUnavailable ) || ( options.shouldRetry && !options.shouldRetry() ) ) {
            throw err;
        }

        await wait( serviceUnavailableRetryDelayMs( err ), options.signal );
        return attempt();
    }
}
